import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import io from 'socket.io-client';
import axios from 'axios';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const isProduction = process.env.NODE_ENV === 'production';
    const useSockets = process.env.REACT_APP_USE_SOCKETS === 'true';
    
    if (isProduction && !useSockets) {
      console.log('📡 BASERRI-ADITU: Socket.IO desactivado en producción (Usando fallback HTTP)');
      setIsConnected(false); // Force fallback
      return;
    }

    const newSocket = io(API_URL, {
      transports: ['polling'],
      reconnection: true,
      reconnectionAttempts: 5, // Reduced to minimize noise
      reconnectionDelay: 5000,
      withCredentials: true
    });

    newSocket.on('connect', () => {
      console.log('Socket connected:', newSocket.id);
      setIsConnected(true);
    });

    newSocket.on('disconnect', () => {
      console.log('Socket disconnected');
      setIsConnected(false);
    });

    newSocket.on('chat_response', (data) => {
      setMessages(prev => [...prev, { type: 'bot', ...data }]);
    });

    // Ganadero → Admin messaging
    newSocket.on('ganadero_message', (data) => {
      console.log('Mensaje de ganadero:', data);
      setMessages(prev => [...prev, { 
        type: 'ganadero', 
        from: 'ganadero',
        ...data 
      }]);
    });

    // Admin → Ganadero response
    newSocket.on('admin_response', (data) => {
      console.log('Respuesta de admin:', data);
      setMessages(prev => [...prev, { 
        type: 'admin', 
        from: 'admin',
        ...data 
      }]);
    });

    // Notification for new ganadero messages
    newSocket.on('new_ganadero_message', (data) => {
      console.log('Nuevo mensaje de ganadero:', data);
      // Trigger notification sound or alert
      if (data.priority === 'alta') {
        alert(`🚨 ALERTA: ${data.ganadero} - ${data.preview}`);
      }
    });

    newSocket.on('voice_response', (data) => {
      console.log('Voice response:', data);
    });

    newSocket.on('alert_notification', (data) => {
      console.log('Alert notification:', data);
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, []);

  // HIGH-RELIABILITY POLLING FALLBACK FOR HOSTINGER
  useEffect(() => {
    let interval;
    const fetchMessages = async () => {
      try {
        const response = await axios.get('/api/mensajes');
        if (response.data && response.data.length > 0) {
           const newMsgs = response.data.map(m => ({
              id: m.id,
              emisor: m.emisor,
              texto: m.texto,
              fecha: m.fecha,
              // Enhanced intent detection for the Voice Demo
              intent: m.texto.toLowerCase().includes('pinta') || m.texto.toLowerCase().includes('naci') ? 'nacimiento' : 
                       m.texto.toLowerCase().includes('txuri') || m.texto.toLowerCase().includes('matadero') ? 'bloqueo' : 
                       m.texto.toLowerCase().includes('vacuna') ? 'vacunacion' : null
           }));
           setMessages(newMsgs);
        }
      } catch(e) {
        console.error('Error in message polling:', e);
      }
    };

    if (!isConnected) {
      console.log('📡 BASERRI-ADITU: Usando fallback de sondeo HTTP (Socket no disponible)');
      fetchMessages(); // Initial fetch
      interval = setInterval(fetchMessages, 2000); // 2 seconds for the wow effect
    }
    return () => clearInterval(interval);
  }, [isConnected]);

  const sendMessage = useCallback(async (message, room = 'general') => {
    if (isConnected && socket) {
      socket.emit('chat_message', { message, room });
    } else {
      // HTTP Fallback
      try {
        await axios.post('/api/mensajes', { emisor: 'ganadero', texto: message });
      } catch(e) { console.error('API Send failed:', e); }
    }
    setMessages(prev => [...prev, { emisor: 'ganadero', texto: typeof message === 'object' ? message.message : message, fecha: new Date().toISOString(), id: Date.now() }]);
  }, [socket, isConnected]);

  const sendVoiceCommand = useCallback((audioData, language = 'eu') => {
    if (isConnected && socket) {
      socket.emit('voice_command', { audio_data: audioData, language });
    }
  }, [socket, isConnected]);

  const joinRoom = useCallback((room) => {
    if (isConnected && socket) {
      socket.emit('join_room', room);
    }
  }, [socket, isConnected]);

  const sendGanaderoMessage = useCallback(async (message, ganaderoInfo, priority = 'media') => {
    const payload = { 
      message, 
      ganadero: ganaderoInfo.nombre || 'Ganadero',
      explotacion: ganaderoInfo.explotacion || 'Sin especificar',
      timestamp: new Date().toISOString(),
      priority,
      preview: message.substring(0, 50) + (message.length > 50 ? '...' : '')
    };

    if (isConnected && socket) {
      socket.emit('ganadero_to_admin', payload);
    } else {
      // HTTP Fallback to consolidated endpoint
      try {
        await axios.post('/api/mensajes', { emisor: 'ganadero', texto: message });
      } catch(e) { console.error('API Send failed:', e); }
    }
    
    // Add user message locally
    setMessages(prev => [...prev, { 
      emisor: 'ganadero', 
      texto: message,
      fecha: new Date().toISOString(),
      id: Date.now() + 1
    }]);
  }, [socket, isConnected]);

  const sendAdminResponse = useCallback(async (response, targetGanadero, requestId) => {
    const payload = { 
      response,
      targetGanadero,
      requestId,
      timestamp: new Date().toISOString()
    };

    if (isConnected && socket) {
      socket.emit('admin_to_ganadero', payload);
    } else {
      try {
        await axios.post('/api/mensajes', { emisor: 'admin', texto: response });
      } catch(e) { console.error('API Admin Response failed:', e); }
    }
    setMessages(prev => [...prev, { emisor: 'admin', texto: response, fecha: new Date().toISOString(), id: Date.now() + 2 }]);
  }, [socket, isConnected]);

  const notifyAdmin = useCallback((notification) => {
    if (isConnected && socket) {
      socket.emit('admin_notification', {
        notification,
        timestamp: new Date().toISOString(),
        type: 'system'
      });
    }
  }, [socket, isConnected]);

  const value = {
    socket,
    isConnected,
    messages,
    sendMessage,
    sendVoiceCommand,
    joinRoom,
    sendGanaderoMessage,
    sendAdminResponse,
    notifyAdmin,
    setMessages
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};
